import { useState, useEffect } from "react";
import LoginPage from './LoginPage';
import Billing from './Billing';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const INK = "#1a1a18";
const INK_MUTED = "#6b6b63";
const INK_FAINT = "#c8c8be";
const PAPER = "#f5f3ee";
const PAPER_WARM = "#ede9e0";
const PAPER_CARD = "#faf9f6";
const BORDER = "#ddd9d0";
const GREEN = "#2d5a3d";
const GREEN_LIGHT = "#e8f0eb";
const GOLD = "#b8860b";
const GOLD_LIGHT = "#fdf6e3";
const BLUE = "#3d6b9a";
const BLUE_LIGHT = "#eef3f9";
const RED = "#8b2020";
const RED_LIGHT = "#fdf0f0";


function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",hour12:true});
}

function lookbackLabel(selectedDate, days) {
  if (!selectedDate) return '';
  const end = new Date(`${selectedDate}T12:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  if (days === 1) return fmt(end);
  return `${fmt(start)} – ${fmt(end)}`;
}

function fmtDuration(start, end) {
  const mins = (new Date(end) - new Date(start)) / 60000;
  return mins >= 60 ? `${(mins/60).toFixed(1)}h` : `${mins}m`;
}

// Map raw error messages to user-friendly language.
// Messages that already end with "Please try again." / "Try again tomorrow." / "sign in again."
// are passed through unchanged so already-friendly throws don't get double-mapped.
function friendlyError(raw) {
  if (!raw) return 'Something went wrong. Please try again.';
  if (/please try again\.|try again tomorrow\.|sign in again\.|connecting again\./i.test(raw)) return raw;
  const m = raw.toLowerCase();
  if (m.includes('select at least one email')) return raw;
  if (m.includes('temporarily limiting') || m.includes('mailboxconcurrency'))
    return 'Microsoft is temporarily limiting data retrieval due to high activity. Please wait a few minutes and try again.';
  if (m.includes('session expired') || /\b401\b/.test(m) || m.includes('unauthorized') || m.includes('not authenticated'))
    return 'Your session has expired. Please sign in again.';
  if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('network request failed') || m.includes('load failed'))
    return 'Unable to connect. Please check your internet connection and try again.';
  if (m.includes('clio'))
    return 'Unable to connect to Clio. Please check your Clio connection in settings and try again.';
  if (m.includes('graph api') || m.includes('microsoft 365'))
    return 'Unable to retrieve your Microsoft 365 data. Please try again.';
  if (/\b500\b/.test(m) || m.includes('internal server'))
    return 'Something went wrong on our end. Please try again.';
  if (/\b429\b/.test(m) || m.includes('rate limit') || m.includes('too many request'))
    return 'Too many requests. Please wait a moment and try again.';
  if (m.includes('json') || m.includes('parse') || m.includes('unexpected token') || m.includes('syntaxerror'))
    return 'Received an unexpected response from the server. Please try again.';
  return 'Something went wrong. Please try again.';
}

// Vague calendar event detection — mirrors server/calendarMatcher.js isVagueEvent
const VAGUE_WORDS_CLIENT = new Set([
  'meeting','lunch','call','sync','standup','catchup',
  'busy','ooo','out','of','office','team',
  'daily','weekly','monthly','annual','checkin','checkins',
]);
function isVagueCalendarEvent(event) {
  const hasAttendees = Array.isArray(event.attendees) && event.attendees.length > 0;
  const hasDescription = Boolean(event.bodyPreview && event.bodyPreview.trim());
  if (hasAttendees || hasDescription) return false;
  const title = (event.subject || '').toLowerCase().replace(/-/g, '');
  const words = title.split(/\s+/).filter(Boolean);
  return words.length === 0 || words.every(w => VAGUE_WORDS_CLIENT.has(w));
}

function buildPrompt(calendar, emails, documents, date, user, emailsOverflow = 0, docsOverflow = 0, lookbackStart = null) {
  const dateLabel = date ? new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric", timeZone:"UTC" }) : new Date().toLocaleDateString("en-US", { weekday:"long", year:"numeric", month:"long", day:"numeric" });
  const activityRange = lookbackStart && lookbackStart !== date
    ? `${lookbackStart} → ${date}`
    : date;

  const calText = calendar.map(e =>
    `[CALENDAR] ${fmtTime(e.start)}–${fmtTime(e.end)} (${fmtDuration(e.start,e.end)}) | "${e.subject}" | Attendees: ${e.attendees.join(", ")} | ${e.isOnlineMeeting?"Teams/Video":"In-person/Phone"} | Notes: "${e.bodyPreview||"none"}"`
  ).join("\n");

  const emailText = emails.map(e =>
    `[EMAIL-${e.folder.toUpperCase()}] ${e.activityDate || ''} ${fmtTime(e.sentAt||e.receivedAt)} | From: ${e.from} | To: ${(e.to||[]).join(", ")} | Subject: "${e.subject}" | Attachments: ${e.hasAttachments?"Yes":"No"} | Preview: "${e.bodyPreview}"`
  ).join("\n");

  const docText = documents.map(d =>
    `[DOCUMENT] ${d.activityDate || ''} ${fmtTime(d.lastModified)} | "${d.name}" | Path: ${d.path} | Size: ${d.sizeKB}KB`
  ).join("\n");

  return `You are an expert legal billing assistant. Your task is to analyze raw activity data from an attorney's Outlook calendar, email, and document management system, then generate professional time entry narratives.

ATTORNEY CONTEXT:
- Name: ${user?.displayName || "Attorney"}
- Email: ${user?.email || ""}
- Billing rate: $300/hour
- Billing date: ${dateLabel}
- Activity window: Calendar shows today's schedule. Emails and documents span ${activityRange} — the period since last billing.

KNOWN CONTACTS (from firm address book):
- dpark@greysonlaw.com = David Park, opposing counsel (Hendricks matter)
- scollins@collinslaw.com = Sarah Collins, co-counsel
- pokafor@gmail.com = Patricia Okafor, client (employment case)
- mtorres@hendrickscorp.com = Mark Torres, General Counsel (Hendricks Corp, client)
- paralegal@mercerassoc.com = Lisa Chen, paralegal
- courtclerk_wawd@uscourts.gov = Federal court clerk, W.D. Washington
- aparker@mercerassoc.com = Associate attorney (internal)
- lchang@mercerassoc.com = Associate attorney (internal)

KNOWN MATTERS (from firm matter management):
- "Hendricks" or "RFP" or "ESI" or "Settlement" or "dpark" or "mtorres" or "hendrickscorp" → Matter 2241: Hendricks Corp v. Pacific Supply Co. (commercial contract dispute)
- "Okafor" or "Pat" or "pokafor" or "Thursday" prep or "depo" or "continuance" or "24-cv-0892" or "Pacific Rim" → Matter 2189: Okafor v. Pacific Rim Logistics (employment discrimination)
- "NHoldings" or "Northgate" or "SJ" or "summary judgment" or "adverse possession" → Matter 2304: Northgate Holdings LLC (real estate litigation)
- Internal team meetings, lunch = Non-billable

RAW ACTIVITY DATA:
${calText}

${emailText}
${emailsOverflow > 0 ? `[Note: ${emailsOverflow} additional emails were not included due to volume limits. Shown emails are the most recent 15 inbox + 15 sent.]` : ''}

${docText}
${docsOverflow > 0 ? `[Note: ${docsOverflow} additional documents were not included due to volume limits. Shown documents are the most recently modified 20.]` : ''}

INSTRUCTIONS:
1. Analyze ALL data sources together — cross-reference emails with calendar events and documents to build a complete picture of each matter worked on
2. Infer matter assignment from contact names, email domains, subject line keywords, document names, and context
3. Group all activity for each matter into a single consolidated time entry
4. Estimate time realistically: calendar events provide exact duration; emails average 6-15 mins each depending on complexity; document review/drafting 15-45 mins per document
5. Write narratives in formal legal billing style: past tense, active voice, specific actions ("Telephone conference with opposing counsel D. Park re ESI production protocol and objections to RFP Set 2; reviewed and responded to correspondence re same")
6. Flag any ambiguous matter assignments with [INFERRED] tag
7. Exclude non-billable activity from billable entries but list separately. Flag time gaps longer than 30 minutes between activities where no billable work is recorded — add a flag to the adjacent entry: "GAP: [time range] (~Xhr) unaccounted". Only flag gaps that fall within business hours (8:00am–6:00pm); ignore gaps outside this window. Each gap time range must appear exactly once in the entire output. If you find yourself writing a gap flag for a time range already flagged elsewhere, do not write it — omit it entirely. Do not write any note saying it was already flagged. If activities overlap in the same time window, do not double-count — add a flag: "OVERLAP: [Activity A] and [Activity B] — time counted once under [chosen activity]." Keep overlap flags under 20 words — no explanations, no context, no paragraphs.

RESPOND WITH VALID JSON ONLY — no markdown, no explanation:
[
  {
    "matter": "Matter 2241 — Hendricks Corp v. Pacific Supply Co.",
    "matterCode": "A",
    "hours": 2.5,
    "confidence": "high",
    "sourcesUsed": [
      {"id": "cal-003", "label": "9:00am calendar event — Call with Pat re: prep"},
      {"id": "email-001", "label": "Email received 8:15am — RE: RFP Set 2 from D. Park"}
    ],
    "narrative": "Your narrative here.",
    "flags": ["GAP: 2:00pm-3:30pm (~1.5hr) unaccounted"]
  }
]

Include one entry per matter plus non-billable. Confidence: high/medium/low based on how clearly activity maps to the matter.`;
}

const MATTER_STYLE = {
  A: { bg:GREEN_LIGHT, border:"#b8d4bf", text:GREEN, label:"Matter 2241 — Hendricks Corp" },
  B: { bg:GOLD_LIGHT, border:"#e0c870", text:GOLD, label:"Matter 2189 — Okafor" },
  C: { bg:BLUE_LIGHT, border:"#b0c8e0", text:BLUE, label:"Matter 2304 — Northgate Holdings" },
  NB: { bg:"#f5f5f5", border:"#ddd", text:"#999", label:"Non-billable" },
};

const CONF_DOT = {
  high: "#4a8c62",
  medium: "#c9940a",
  low: RED,
};

function Tag({ color, bg, border, children }) {
  return (
    <span style={{ display:"inline-block", fontFamily:"monospace", fontSize:9, background:bg, color, border:`1px solid ${border}`, borderRadius:3, padding:"1px 6px", letterSpacing:"0.04em", marginRight:4 }}>
      {children}
    </span>
  );
}

function SectionHeader({ title, count, color = INK_MUTED }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px", background:PAPER_WARM, borderBottom:`1px solid ${BORDER}` }}>
      <span style={{ fontFamily:"monospace", fontSize:10, textTransform:"uppercase", letterSpacing:"0.1em", color:INK_MUTED }}>{title}</span>
      <span style={{ fontFamily:"monospace", fontSize:10, color }}>{count} items</span>
    </div>
  );
}

function MatterTypeahead({ value, matters, textColor, onSave, onCancel }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen]   = useState(false);

  const q = query.toLowerCase();
  const results = (matters && query.length >= 2)
    ? matters
        .filter(m =>
          (m.number      || '').toLowerCase().includes(q) ||
          (m.client      || '').toLowerCase().includes(q) ||
          (m.description || '').toLowerCase().includes(q)
        )
        .slice(0, 20)
    : [];

  function select(m) {
    onSave([m.number, m.description].filter(Boolean).join(' — '));
  }

  return (
    <div style={{position:'relative'}}>
      <input
        autoFocus
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { if (query.length >= 2) setOpen(true); }}
        onBlur={() => { setOpen(false); onSave(query); }}
        onKeyDown={e => {
          if (e.key === 'Enter')  { setOpen(false); onSave(query); }
          if (e.key === 'Escape') { setOpen(false); onCancel(); }
        }}
        style={{width:'100%',fontFamily:'sans-serif',fontSize:13,fontWeight:700,color:textColor||INK,border:`1px solid ${GOLD}`,borderRadius:3,padding:'3px 6px',background:GOLD_LIGHT,outline:'none',boxSizing:'border-box'}}
      />
      {open && results.length > 0 && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:200,background:'white',border:`1px solid ${BORDER}`,borderRadius:4,boxShadow:'0 4px 12px rgba(0,0,0,0.12)',maxHeight:240,overflowY:'auto',marginTop:2}}>
          {results.map(m => (
            <div
              key={m.id}
              onMouseDown={e => { e.preventDefault(); select(m); }}
              style={{padding:'7px 10px',cursor:'pointer',borderBottom:`1px solid ${BORDER}`}}
              onMouseEnter={e => e.currentTarget.style.background = GOLD_LIGHT}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >
              <div style={{fontFamily:'sans-serif',fontSize:12,fontWeight:600,color:INK}}>{m.description}</div>
              <div style={{fontFamily:'monospace',fontSize:10,color:INK_MUTED,marginTop:2}}>{m.number}{m.client ? ` · ${m.client}` : ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState("calendar");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generateStatus, setGenerateStatus] = useState('');
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState(false);
  const [approved, setApproved] = useState({});
  const [editing, setEditing] = useState({});
  const [editText, setEditText] = useState({});
  const [editingHours, setEditingHours] = useState({});
  const [editHoursText, setEditHoursText] = useState({});
  const [editingMatter, setEditingMatter] = useState({});
  const [editMatterText, setEditMatterText] = useState({});
  const [editingRate, setEditingRate] = useState(false);
  const [editRateText, setEditRateText] = useState("");
  const [copied, setCopied] = useState({});
  const [showReasoning, setShowReasoning] = useState({});
  const [showFullFlag, setShowFullFlag] = useState({});

  const [loginError] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('auth_error') || '';
    if (err) window.history.replaceState({}, '', window.location.pathname);
    return err ? decodeURIComponent(err) : '';
  });

  const [reconnectSuccess, setReconnectSuccess] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reconnect') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      return true;
    }
    return false;
  });

  const [pendingDigestSync, setPendingDigestSync] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("digest") ? p.get("date") : null;
  });
  const [digestFlowPhase, setDigestFlowPhase] = useState(null); // null | 'syncing' | 'generating'

  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(
    () => localStorage.getItem('gettingStartedDismissed') === 'true'
  );

  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ digest_enabled: false, digest_time: '18:00', timezone: 'America/New_York', email_lookback_days: 3, doc_lookback_days: 3, hourly_rate: 300 });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [cacheClearLoading, setCacheClearLoading] = useState(false);
  const [cacheClearMsg, setCacheClearMsg] = useState('');

  const [calendar, setCalendar] = useState([]);
  const [emails, setEmails] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [emailChecked, setEmailChecked] = useState({});       // { emailId: bool }
  const [docChecked, setDocChecked] = useState({});           // { docId: bool }
  const [vagueEventBannerDismissed, setVagueEventBannerDismissed] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');

  // Microsoft 365 connection
  const [auth, setAuth] = useState(null);        // null = unknown, false = not authed, object = user
  const [authLoading, setAuthLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [msTokenStatus, setMsTokenStatus] = useState(null); // null=unknown, true=has refresh token, false=missing
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  });
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [lookbackStart, setLookbackStart] = useState(null);

  // Clio connection
  const [clioAuth, setClioAuth] = useState(null);
  const [clioAuthLoading, setClioAuthLoading] = useState(true);
  const [clioMatters, setClioMatters] = useState([]);
  const [pushModal, setPushModal] = useState(null); // { entryIdx, matterId }
  const [pushing, setPushing] = useState({});
  const [pushResult, setPushResult] = useState({});

  const billable = entries.filter(e => e.matterCode !== "NB");
  const totalHours = billable.reduce((s, e) => s + e.hours, 0);
  const hourlyRate = prefs.hourly_rate || 300;
  const totalValue = Math.round(totalHours * hourlyRate);

  const vagueEvents = calendar.filter(isVagueCalendarEvent);
  const checkedEmailCount = emails.filter(e => emailChecked[e.id] === true).length;
  const checkedDocCount = documents.filter(d => docChecked[d.id] === true).length;

  function deleteItem(type, index) {
    if (type === "calendar") setCalendar(prev => prev.filter((_,i) => i!==index));
    else if (type === "email") setEmails(prev => prev.filter((_,i) => i!==index));
    else setDocuments(prev => prev.filter((_,i) => i!==index));
  }

  function handleDateChange(newDate) {
    setSelectedDate(newDate);
    setCalendar([]);
    setEmails([]);
    setDocuments([]);
    setEntries([]);
    setGenerated(false);
    setApproved({});
    setEditing({});
    setEditText({});
    setEditingHours({});
    setEditHoursText({});
    setEditingMatter({});
    setEditMatterText({});
    setCopied({});
    setShowReasoning({});
    setShowFullFlag({});
    setPushResult({});
    setLookbackStart(null);
    setEmailChecked({});
    setDocChecked({});
    setVagueEventBannerDismissed(false);
  }

  // Check auth status on mount (and after redirect back from Microsoft or Clio)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(r => r.unregister());
      });
    }

    // Handle reconnect link from email — /auth/microsoft?reconnect=true hits the SPA;
    // forward to the server's OAuth endpoint to start the reconnect flow.
    if (window.location.pathname === '/auth/microsoft') {
      window.location.replace(`${API_URL}/auth/microsoft?reconnect=true`);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    async function checkAuth() {
      try {
        const res = await fetch(`${API_URL}/auth/status`, { credentials: "include" });
        const data = await res.json();
        setAuth(data.authenticated ? data.user : false);
        setIsImpersonating(!!data.isImpersonating);
        if (urlParams.get("auth_error")) {
          console.error('[auth] Microsoft auth error:', urlParams.get("auth_error"));
          setSyncError("We couldn't complete the Microsoft sign-in. Please try connecting again.");
        }
        return data.authenticated;
      } catch {
        setAuth(false);
        return false;
      } finally {
        setAuthLoading(false);
      }
    }

    async function checkClioAuth() {
      try {
        const res = await fetch(`${API_URL}/clio/status`, { credentials: "include" });
        const data = await res.json();
        setClioAuth(data.authenticated ? data.user : false);
        if (urlParams.get("clio_error")) {
          console.error('[auth] Clio auth error:', urlParams.get("clio_error"));
          setSyncError("We couldn't complete the Clio sign-in. Please try connecting again.");
        }
      } catch {
        setClioAuth(false);
      } finally {
        setClioAuthLoading(false);
      }
    }

    Promise.all([checkAuth(), checkClioAuth()]).then(async ([msAuthed]) => {
      // After Microsoft login, consume the saved destination and redirect there
      console.log('[redirectAfterLogin] msAuthed:', msAuthed, '| stored dest:', sessionStorage.getItem('redirectAfterLogin'));
      if (msAuthed && !urlParams.get("auth_error")) {
        const dest = sessionStorage.getItem('redirectAfterLogin');
        if (dest) {
          console.log('[redirectAfterLogin] redirecting to:', dest);
          sessionStorage.removeItem('redirectAfterLogin');
          window.location.replace(dest);
          return;
        }
      }

      if (urlParams.get("auth_error") || urlParams.get("clio_error")) {
        // window.history.replaceState({}, "", window.location.pathname); // temporarily disabled for auth debugging
      }

      // Load preferences on mount so rate box and other UI reflect saved values
      try {
        const prefRes = await fetch(`${API_URL}/api/preferences`, { credentials: 'include', cache: 'no-store' });
        if (prefRes.ok) setPrefs(await prefRes.json());
      } catch {}

      // Digest deep-link: set date, schedule auto-sync, mark reviewed
      const digestToken = urlParams.get("digest");
      const digestDate  = urlParams.get("date");
      if (digestToken && digestDate) {
        window.history.replaceState({}, "", window.location.pathname);
        setSelectedDate(digestDate);
        setCalendar([]); setEmails([]); setDocuments([]);
        setEntries([]); setGenerated(false);
        fetch(`${API_URL}/api/digest/${digestToken}/reviewed`, { method: "PUT", credentials: "include" })
          .catch(err => console.warn("[digest] Mark reviewed failed:", err.message));
      }
    });
  }, []);

  // Fetch MS refresh token status whenever auth changes
  useEffect(() => {
    if (!auth) { setMsTokenStatus(null); return; }
    fetch(`${API_URL}/api/auth/ms-token-status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => setMsTokenStatus(data?.hasRefreshToken ?? null))
      .catch(() => setMsTokenStatus(null));
  }, [auth]);

  // Digest deep-link: auto-sync then auto-generate once auth and date are ready
  useEffect(() => {
    if (pendingDigestSync && auth && selectedDate === pendingDigestSync) {
      setPendingDigestSync(null);
      runDigestAutoFlow();
    }
  }, [auth, selectedDate, pendingDigestSync]);

  // Trigger generate() as the second phase of the digest auto-flow
  useEffect(() => {
    if (digestFlowPhase === 'generating') {
      generate(true);
    }
  }, [digestFlowPhase]);

  async function runDigestAutoFlow() {
    setDigestFlowPhase('syncing');
    await syncFromMicrosoft();
    setDigestFlowPhase('generating');
  }

  async function syncFromMicrosoft() {
    setSyncing(true);
    setSyncStatus('Retrieving your calendar, emails and documents...');
    setSyncError("");
    try {
      const tzOffset = new Date().getTimezoneOffset();
      const [calRes, emailRes, docRes] = await Promise.all([
        fetch(`${API_URL}/graph/calendar?date=${selectedDate}&tzOffset=${tzOffset}`, { credentials: "include" }),
        fetch(`${API_URL}/graph/emails?date=${selectedDate}&tzOffset=${tzOffset}`, { credentials: "include" }),
        fetch(`${API_URL}/graph/documents?date=${selectedDate}&tzOffset=${tzOffset}`, { credentials: "include" }),
      ]);
      setSyncStatus('Matching to calendar events...');

      // Check for any auth errors (e.g. session expired)
      for (const res of [calRes, emailRes, docRes]) {
        if (res.status === 401) {
          setAuth(false);
          throw new Error("Your session has expired. Please sign in again.");
        }
        if (!res.ok) throw new Error("Unable to retrieve your Microsoft 365 data. Please try again.");
      }

      const [calData, emailData, docData] = await Promise.all([
        calRes.json(), emailRes.json(), docRes.json(),
      ]);

      setCalendar(calData.events || []);
      setEmails(emailData.emails || []);
      const initChecked = {};
      (emailData.emails || []).forEach(e => { initChecked[e.id] = Boolean(e.matched); });
      setEmailChecked(initChecked);
      const initDocChecked = {};
      (docData.documents || []).forEach(d => { initDocChecked[d.id] = Boolean(d.matched); });
      setDocChecked(initDocChecked);
      setVagueEventBannerDismissed(false);
      setError("");   // clear any pre-sync error (e.g. stale "Select at least one email")
      setDocuments(docData.documents || []);
      setLookbackStart(emailData.lookbackStart || docData.lookbackStart || selectedDate);

      // Reset generated entries since data changed
      setEntries([]);
      setGenerated(false);
      setApproved({});
      setEditing({});
      setEditText({});
      setEditingHours({});
      setEditHoursText({});
      setEditingMatter({});
      setEditMatterText({});
      setCopied({});
      setShowReasoning({});
      setShowFullFlag({});
      setPushResult({});
    } catch (e) {
      console.error('[sync]', e.message);
      setSyncError(friendlyError(e.message));
    }
    setSyncStatus('');
    setSyncing(false);
  }

  const STATUS_LABELS = {
    retrieving:  'Retrieving your activity...',
    filtering:   'Filtering emails and documents...',
    matching:    'Matching to calendar events...',
    summarizing: 'Summarizing emails and documents...',
    generating:  'Generating time entries...',
    finalizing:  'Finalizing your time entries...',
  };

  async function generate(autoFlow = false) {
    setLoading(true);
    setGenerateStatus('');
    setError("");
    setEntries([]);
    setGenerated(false);
    setApproved({});
    setEditing({});
    setEditText({});
    setEditingHours({});
    setEditHoursText({});
    setEditingMatter({});
    setEditMatterText({});
    setCopied({});
    setShowReasoning({});
    setShowFullFlag({});
    setPushResult({});
    try {
      // Only send checked emails.
      // If no emails are loaded yet, exit silently — the "Awaiting analysis" empty
      // state covers that case. Only show the validation message when emails are
      // loaded but the attorney has unchecked all of them.
      const checkedEmails = emails.filter(e => emailChecked[e.id] === true);
      if (checkedEmails.length === 0) {
        if (emails.length > 0) {
          setError("Select at least one email to generate time entries.");
        }
        setLoading(false);
        return;
      }

      // Cap activity volume — most recent first so important afternoon work is never dropped
      const calCapped = calendar.slice(0, 20); // Graph already returns asc by start time

      const inboxDesc = checkedEmails.filter(e => e.folder === 'inbox')
        .sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt));
      const sentDesc  = checkedEmails.filter(e => e.folder === 'sent')
        .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
      const emailsCapped = [...inboxDesc.slice(0, 15), ...sentDesc.slice(0, 15)]
        .sort((a, b) => new Date(a.receivedAt || a.sentAt) - new Date(b.receivedAt || b.sentAt));
      const emailsOverflow = Math.max(0, checkedEmails.length - emailsCapped.length);

      const checkedDocs = documents.filter(d => docChecked[d.id] === true);
      const docsCapped = [...checkedDocs]
        .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
        .slice(0, 20);
      const docsOverflow = Math.max(0, checkedDocs.length - docsCapped.length);

      let parsed;
      if (clioAuth) {
        // NDJSON streaming — server emits status events then a result event
        const res = await fetch(`${API_URL}/clio/generate`, {
          method:"POST",
          headers:{"Content-Type":"application/json"},
          credentials:"include",
          body:JSON.stringify({ calendar:calCapped, emails:emailsCapped, documents:docsCapped, emailsOverflow, docsOverflow, date:selectedDate, lookbackStart, model:"claude-sonnet-4-6", max_tokens:8000 })
        });
        if (res.status === 429) { const d = await res.json(); throw new Error(d.message || 'Daily analysis limit reached. Try again tomorrow.'); }
        if (!res.ok) throw new Error('Unable to generate time entries. Please try again.');
        const text = await res.text();
        let data = null;
        for (const line of text.split('\n')) {
          if (!line.trim()) continue;
          const evt = JSON.parse(line);
          if (evt.type === 'status') { setGenerateStatus(evt.step); }
          else if (evt.type === 'error') { throw new Error(friendlyError(evt.message) || 'Unable to generate time entries. Please try again.'); }
          else if (evt.type === 'result') { data = evt.data; }
        }
        if (!data) throw new Error('Unable to generate time entries. Please try again.');
        if (data.clioMatters) setClioMatters(data.clioMatters);
        const raw = data.content[0].text.replace(/^```json\s*/i,"").replace(/\s*```$/,"").trim();
        try { parsed = JSON.parse(raw); } catch { throw new Error('Unable to parse response. Please try again.'); }
      } else {
        // Fallback — use demo hardcoded matter context
        const res = await fetch(`${API_URL}/generate`, {
          method:"POST",
          headers:{"Content-Type":"application/json"},
          credentials:"include",
          body:JSON.stringify({
            model:"claude-sonnet-4-6",
            max_tokens:4000,
            messages:[{role:"user",content:buildPrompt(calCapped, emailsCapped, docsCapped, selectedDate, auth, emailsOverflow, docsOverflow, lookbackStart)}]
          })
        });
        if (res.status === 429) { const d = await res.json(); throw new Error(d.message || 'Daily analysis limit reached. Try again tomorrow.'); }
        if (!res.ok) throw new Error('Unable to generate time entries. Please try again.');
        const data = await res.json();
        const raw = data.content[0].text.replace(/^```json\s*/i,"").replace(/\s*```$/,"").trim();
        try { parsed = JSON.parse(raw); } catch { throw new Error('Unable to parse response. Please try again.'); }
      }
      // Stamp narrativeGenerated on each entry so we can detect edits at approve time
      setEntries(parsed.map(e => ({ ...e, narrativeGenerated: e.narrative })));
      setGenerated(true);
    } catch(e) {
      console.error('[generate]', e.message);
      setError(friendlyError(e.message));
    }
    setLoading(false);
    setGenerateStatus('');
    if (autoFlow) setDigestFlowPhase(null);
  }

  function logEntry(entry, editType, overrideNarrativeFinal) {
    const narrativeFinal = editType === 'rejected' ? null : (overrideNarrativeFinal ?? entry.narrative);
    // entry.hours already reflects any in-UI edit (toggleEditHours commits back
    // into entries state). Server accepts a finite number 0–24 or null.
    const hoursNum = Number(entry.hours);
    fetch(`${API_URL}/api/log-entry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        narrative_generated: entry.narrativeGenerated || entry.narrative,
        narrative_final: narrativeFinal,
        edit_type: editType,
        source_activities: entry.sourcesUsed || [],
        matter_id: entry.clioMatterId || entry.matterCode || null,
        matter_name: entry.matter || '',
        date: selectedDate,
        hours: Number.isFinite(hoursNum) && hoursNum >= 0 && hoursNum <= 24 ? hoursNum : null,
      }),
    }).catch(err => console.warn('[log-entry] failed to send:', err.message)); // fire and forget
  }

  async function pushToClio(entryIdx, matterId) {
    if (!matterId) return;
    setPushing(p => ({...p,[entryIdx]:true}));
    setPushResult(r => ({...r,[entryIdx]:null}));
    const entry = entries[entryIdx];
    try {
      const res = await fetch(`${API_URL}/clio/time-entries`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        credentials:"include",
        body:JSON.stringify({ matter_id: matterId, date: selectedDate, hours: entry.hours, narrative: entry.narrative })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(friendlyError(data.error || `Clio API ${res.status}`));
      setPushResult(r => ({...r,[entryIdx]:{ success:true, clioUrl:data.clioUrl, entryId:data.entryId }}));
      logEntry(entries[entryIdx], 'pushed_to_clio');
    } catch(e) {
      console.error('[push]', e.message);
      setPushResult(r => ({...r,[entryIdx]:{ success:false, error: friendlyError(e.message) }}));
    }
    setPushing(p => ({...p,[entryIdx]:false}));
    setPushModal(null);
  }

  function toggleApprove(i) {
    const nowApproving = !approved[i];
    setApproved(a => ({...a,[i]:nowApproving}));
    if (nowApproving) {
      const entry = entries[i];
      const wasEdited = entry.narrative !== entry.narrativeGenerated;
      logEntry(entry, wasEdited ? 'edited' : 'accepted');
    }
  }
  function toggleEdit(i) {
    if (editing[i]) {
      setEntries(prev => prev.map((e,idx) => idx===i ? {...e,narrative:editText[i]??e.narrative} : e));
      setEditing(ed => ({...ed,[i]:false}));
    } else {
      setEditText(t => ({...t,[i]:entries[i].narrative}));
      setEditing(ed => ({...ed,[i]:true}));
    }
  }
  function saveMatter(i) {
    const val = (editMatterText[i] ?? entries[i].matter).trim();
    if (val) setEntries(prev => prev.map((e,idx) => idx===i ? {...e,matter:val} : e));
    setEditingMatter(m => ({...m,[i]:false}));
  }
  async function saveRate(newRate) {
    setPrefs(p => ({ ...p, hourly_rate: newRate }));
    try {
      await fetch(`${API_URL}/api/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...prefs, hourly_rate: newRate }),
      });
    } catch (err) {
      console.warn('[rate] failed to save:', err.message);
    }
  }
  function toggleEditRate() {
    if (editingRate) {
      const parsed = parseInt(editRateText);
      if (!isNaN(parsed) && parsed >= 25) saveRate(parsed);
      setEditingRate(false);
    } else {
      setEditRateText(String(prefs.hourly_rate || 300));
      setEditingRate(true);
    }
  }
  function toggleEditHours(i) {
    if (editingHours[i]) {
      const parsed = parseFloat(editHoursText[i]);
      if (!isNaN(parsed) && parsed > 0) {
        setEntries(prev => prev.map((e,idx) => idx===i ? {...e,hours:Math.round(parsed*10)/10} : e));
      }
      setEditingHours(h => ({...h,[i]:false}));
    } else {
      setEditHoursText(t => ({...t,[i]:entries[i].hours.toFixed(1)}));
      setEditingHours(h => ({...h,[i]:true}));
    }
  }
  function copyEntry(i) {
    const e = entries[i];
    navigator.clipboard.writeText(`${e.matter}\n${e.hours.toFixed(1)} hrs\n${e.narrative}`);
    setCopied(c => ({...c,[i]:true}));
    setTimeout(() => setCopied(c => ({...c,[i]:false})),1500);
  }
  function copyAll() {
    navigator.clipboard.writeText(entries.map(e=>`${e.matter}\n${e.hours.toFixed(1)} hrs\n${e.narrative}`).join("\n\n---\n\n"));
    setCopied(c => ({...c,all:true}));
    setTimeout(() => setCopied(c => ({...c,all:false})),1500);
  }
  function dismissEntry(i) {
    logEntry(entries[i], 'rejected');
    setEntries(prev => prev.filter((_,idx) => idx !== i));
    setApproved(a => { const n={...a}; delete n[i]; return n; });
    setEditing(e => { const n={...e}; delete n[i]; return n; });
    setEditText(t => { const n={...t}; delete n[i]; return n; });
    setEditingHours(h => { const n={...h}; delete n[i]; return n; });
    setEditHoursText(t => { const n={...t}; delete n[i]; return n; });
    setEditingMatter(m => { const n={...m}; delete n[i]; return n; });
    setEditMatterText(t => { const n={...t}; delete n[i]; return n; });
    setCopied(c => { const n={...c}; delete n[i]; return n; });
    setShowReasoning(s => { const n={...s}; delete n[i]; return n; });
    setShowFullFlag(f => { const n={...f}; delete n[i]; return n; });
    setPushResult(r => { const n={...r}; delete n[i]; return n; });
  }
  function exportCSV() {
    const rows=[["Matter","Hours","Confidence","Narrative"],...entries.map(e=>[e.matter,e.hours.toFixed(1),e.confidence,`"${e.narrative.replace(/"/g,'""')}"`])];
    const a=document.createElement("a");
    a.href="data:text/csv,"+encodeURIComponent(rows.map(r=>r.join(",")).join("\n"));
    a.download=`timesheet-${selectedDate}.csv`;
    a.click();
  }

  async function openSettings() {
    setShowSettings(true);
    setPrefsLoading(true);
    setCacheClearMsg('');
    try {
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000));
      const res = await Promise.race([
        fetch(`${API_URL}/api/preferences`, { credentials: 'include', cache: 'no-store' }),
        timeout,
      ]);
      if (res.ok && res.status !== 304) {
        const jsonTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error('json timeout')), 5000));
        const data = await Promise.race([res.json(), jsonTimeout]);
        if (!data.created_at) {
          const supported = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'];
          const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (supported.includes(detected)) data.timezone = detected;
        }
        setPrefs(data);
      }
    } catch (err) {
      console.warn('[settings] failed to load prefs:', err.message);
    }
    setPrefsLoading(false);
  }

  async function savePrefs() {
    try {
      const res = await fetch(`${API_URL}/api/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(prefs),
      });
      if (res.ok) {
        const saved = await res.json();
        setPrefs(saved);
        setShowSettings(false);
      }
    } catch (err) {
      console.warn('[settings] failed to save prefs:', err.message);
    }
  }


  async function clearCacheAll() {
    const confirmed = window.confirm(
      'This will clear your learned email filters. 2ndCounsel will relearn your email patterns on the next analysis. Continue?'
    );
    if (!confirmed) return;
    setCacheClearLoading(true);
    setCacheClearMsg('');
    try {
      const res = await fetch(`${API_URL}/api/cache/all`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCacheClearMsg('Email filters cleared. Your next analysis will relearn your email patterns.');
    } catch (err) {
      console.error('[cache] clear all failed:', err.message);
      setCacheClearMsg('Failed to clear cache. Please try again.');
    }
    setCacheClearLoading(false);
  }

  const tabStyle = (t) => ({
    padding:"8px 14px", fontFamily:"monospace", fontSize:10, letterSpacing:"0.06em",
    textTransform:"uppercase", cursor:"pointer", border:"none",
    borderBottom: tab===t ? `2px solid ${GREEN}` : "2px solid transparent",
    background:"transparent", color: tab===t ? GREEN : INK_MUTED,
    fontWeight: tab===t ? 500 : 400, transition:"all 0.15s"
  });

  if (!authLoading && !clioAuthLoading && !auth && !clioAuth) {
    return <LoginPage error={loginError} />;
  }

  // Non-whitelisted users have a limited session — lock them to /billing
  if (auth?.pendingSubscription && window.location.pathname !== '/billing') {
    window.location.replace('/billing');
    return null;
  }

  if (window.location.pathname === '/billing') {
    const userEmail = auth?.email || clioAuth?.email || '';
    const firmName  = auth?.displayName || clioAuth?.displayName || userEmail;
    const firmId    = userEmail;
    return <Billing firmId={firmId} firmName={firmName} userEmail={userEmail} />;
  }

  return (
    <div style={{fontFamily:"'Georgia',serif",background:PAPER,minHeight:"100vh",fontSize:14}}>

      {/* Header */}
      <div style={{background:INK,padding:"14px 28px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontFamily:"Georgia,serif",fontSize:18,color:PAPER,letterSpacing:"0.02em"}}>
          2nd<span style={{color:"#4a8c62"}}>Counsel</span>
          <span style={{fontFamily:"monospace",fontSize:10,color:"#666",marginLeft:12,letterSpacing:"0.06em"}}>ATTORNEY INTELLIGENCE PLATFORM</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>

          {/* Microsoft 365 status */}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {authLoading ? (
              <span style={{fontFamily:"monospace",fontSize:10,color:"#666",letterSpacing:"0.06em"}}>Checking M365...</span>
            ) : auth ? (
              <>
                <div style={{width:6,height:6,borderRadius:"50%",background: msTokenStatus === false ? "#c9913a" : "#4a8c62"}}></div>
                <span style={{fontFamily:"monospace",fontSize:10,color: msTokenStatus === false ? "#c9913a" : "#4a8c62",letterSpacing:"0.05em"}}>
                  {msTokenStatus === false ? "Microsoft 365 Connected (digest inactive)" : "Microsoft 365 Connected"}
                </span>
                <button
                  onClick={() => fetch(`${API_URL}/auth/logout`, { credentials:"include" }).then(() => { setAuth(false); setMsTokenStatus(null); })}
                  style={{fontFamily:"monospace",fontSize:9,padding:"3px 10px",borderRadius:3,border:"1px solid #444",background:"transparent",color:"#888",cursor:"pointer",letterSpacing:"0.05em"}}
                >Disconnect</button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { sessionStorage.setItem('redirectAfterLogin', window.location.href); window.location.href = `${API_URL}/auth/login`; }}
                  style={{fontFamily:"monospace",fontSize:9,padding:"4px 12px",borderRadius:3,border:"1px solid #4a8c62",background:"#1e3a2a",color:"#4a8c62",cursor:"pointer",letterSpacing:"0.05em"}}
                >Connect Microsoft 365</button>
              </>
            )}
          </div>

          {/* Divider */}
          <div style={{width:1,height:20,background:"#333"}}></div>

          {/* Clio status */}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {clioAuthLoading ? (
              <span style={{fontFamily:"monospace",fontSize:10,color:"#666",letterSpacing:"0.06em"}}>Checking Clio...</span>
            ) : clioAuth ? (
              <>
                <div style={{width:6,height:6,borderRadius:"50%",background:"#7ba7d4"}}></div>
                <span style={{fontFamily:"monospace",fontSize:10,color:"#7ba7d4",letterSpacing:"0.05em"}}>Clio Connected</span>
                <button
                  onClick={() => fetch(`${API_URL}/clio/logout`, { credentials:"include" }).then(() => { setClioAuth(false); setClioMatters([]); })}
                  style={{fontFamily:"monospace",fontSize:9,padding:"3px 10px",borderRadius:3,border:"1px solid #444",background:"transparent",color:"#888",cursor:"pointer",letterSpacing:"0.05em"}}
                >Disconnect</button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { window.location.href = `${API_URL}/clio/login`; }}
                  style={{fontFamily:"monospace",fontSize:9,padding:"4px 12px",borderRadius:3,border:"1px solid #7ba7d4",background:"#1a2a3a",color:"#7ba7d4",cursor:"pointer",letterSpacing:"0.05em"}}
                >Connect Clio</button>
              </>
            )}
          </div>

          {/* Divider */}
          <div style={{width:1,height:20,background:"#333"}}></div>

          {/* Settings */}
          <button
            onClick={openSettings}
            title="Settings"
            style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:"#888",padding:"2px 4px",lineHeight:1,transition:"color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.color="#ccc"}
            onMouseLeave={e=>e.currentTarget.style.color="#888"}
          >⚙️</button>

          {/* Sign out */}
          <button
            onClick={() => fetch(`${API_URL}/auth/logout`, { credentials:'include' }).then(() => { setAuth(false); window.location.href = '/'; })}
            style={{background:"none",border:"none",cursor:"pointer",fontFamily:"monospace",fontSize:9,color:"#666",padding:"2px 4px",letterSpacing:"0.05em",transition:"color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.color="#ccc"}
            onMouseLeave={e=>e.currentTarget.style.color="#666"}
          >Sign out</button>

        </div>
      </div>

      {/* Admin impersonation banner */}
      {isImpersonating && (
        <div style={{background:"#8b2020",borderBottom:"1px solid #6b1818",padding:"10px 28px",display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontFamily:"monospace",fontSize:11,color:"#f8d7d7",letterSpacing:"0.06em",textTransform:"uppercase",fontWeight:700,flexShrink:0}}>ADMIN MODE</span>
          <span style={{fontFamily:"sans-serif",fontSize:13,color:"#f8d7d7",flex:1}}>
            Impersonating <strong>{auth?.email}</strong>
          </span>
          <button
            onClick={async () => {
              await fetch(`${API_URL}/auth/impersonate/end`, { method:'POST', credentials:'include' });
              window.location.href = '/admin';
            }}
            style={{background:"rgba(0,0,0,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:4,padding:"4px 12px",fontFamily:"sans-serif",fontSize:12,color:"#f8d7d7",cursor:"pointer",flexShrink:0}}
          >Exit impersonation</button>
        </div>
      )}

      {/* Reconnect success banner */}
      {reconnectSuccess && (
        <div style={{background:"#1e3a2a",borderBottom:"1px solid #2d5a3d",padding:"12px 28px",display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"#4a8c62",flexShrink:0}}></div>
          <span style={{fontFamily:"sans-serif",fontSize:13,color:"#a8d5b8",flex:1}}>
            Your Microsoft 365 account has been reconnected. Your daily digest will resume at your next scheduled time.
          </span>
          <button
            onClick={() => setReconnectSuccess(false)}
            style={{background:"none",border:"none",cursor:"pointer",fontFamily:"monospace",fontSize:12,color:"#4a8c62",padding:"0 4px",lineHeight:1,transition:"color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.color="#a8d5b8"}
            onMouseLeave={e=>e.currentTarget.style.color="#4a8c62"}
            aria-label="Dismiss"
          >✕</button>
        </div>
      )}

      {/* Digest auto-flow status banner */}
      {digestFlowPhase && (
        <div style={{background:"#1e3a2a",borderBottom:"1px solid #2d5a3d",padding:"12px 28px",display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:11,height:11,border:"2px solid #2d5a3d",borderTopColor:"#4a8c62",borderRadius:"50%",animation:"spin 0.7s linear infinite",flexShrink:0}}></div>
          <span style={{fontFamily:"sans-serif",fontSize:13,color:"#a8d5b8",flex:1}}>
            {digestFlowPhase === 'syncing'
              ? `Pulling fresh activity from Microsoft 365 for ${selectedDate} to make sure your entries are up to date...`
              : `Generating your time entries with the latest activity...`}
          </span>
        </div>
      )}

      <div style={{maxWidth:1180,margin:"0 auto",padding:"20px 20px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,alignItems:"start"}}>

        {/* LEFT — Raw Data Sources */}
        <div>
          {/* Attorney card */}
          <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:6,padding:"12px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:40,height:40,borderRadius:"50%",background:GREEN,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:13,fontFamily:"Georgia,serif",flexShrink:0}}>
              {auth ? auth.displayName.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase() : <span style={{fontFamily:"Georgia,serif",fontSize:11,color:"white",letterSpacing:"0.02em"}}>2C</span>}
            </div>
            <div style={{flex:1}}>
              <div style={{fontFamily:"Georgia,serif",fontSize:15,color:INK}}>
                {auth ? auth.displayName : <><span style={{color:GOLD}}>2nd</span><span style={{color:GREEN}}>Counsel</span></>}
              </div>
              <div style={{fontSize:11,color:INK_MUTED,fontFamily:"sans-serif"}}>{auth ? auth.email : "Attorney Intelligence Platform"}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <input
                type="date"
                value={selectedDate}
                onChange={e => handleDateChange(e.target.value)}
                max={new Date().toLocaleDateString('en-CA')}
                min={new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA')}
                style={{fontFamily:"monospace",fontSize:10,color:GOLD,letterSpacing:"0.04em",border:"none",background:"transparent",cursor:"pointer",textAlign:"right",outline:"none"}}
              />
              <div style={{fontSize:11,color:INK_MUTED,fontFamily:"sans-serif",marginTop:2}}>{calendar.length} cal · {emails.length} emails · {documents.length} docs</div>
            </div>
          </div>

          {/* Sync bar */}
          <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:6,padding:"10px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
            {auth ? (
              <>
                <button
                  onClick={syncFromMicrosoft}
                  disabled={syncing}
                  style={{fontFamily:"monospace",fontSize:10,padding:"5px 14px",borderRadius:4,border:`1px solid ${GREEN}`,background:syncing?PAPER_WARM:GREEN,color:syncing?INK_MUTED:"white",cursor:syncing?"default":"pointer",letterSpacing:"0.05em",transition:"all 0.15s"}}
                >
                  {syncing ? "Syncing..." : "Sync from Microsoft 365"}
                </button>
                <span style={{fontSize:11,color:INK_MUTED,fontFamily:"sans-serif"}}>
                  Pull live calendar, email & documents for {selectedDate}
                </span>
              </>
            ) : (
              <>
                <button
                  onClick={() => { sessionStorage.setItem('redirectAfterLogin', window.location.href); window.location.href = `${API_URL}/auth/login`; }}
                  style={{fontFamily:"monospace",fontSize:10,padding:"5px 14px",borderRadius:4,border:`1px solid ${GREEN}`,background:GREEN,color:"white",cursor:"pointer",letterSpacing:"0.05em"}}
                >Connect Microsoft 365</button>
                <span style={{fontSize:11,color:INK_MUTED,fontFamily:"sans-serif",fontStyle:"italic"}}>Connect Microsoft 365 to pull live data.</span>
              </>
            )}
          </div>
          {syncing && syncStatus && (
            <div style={{marginBottom:8,display:"flex",alignItems:"center",gap:8,padding:"2px 4px",fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>
              <div style={{width:11,height:11,border:`2px solid ${BORDER}`,borderTopColor:GREEN,borderRadius:"50%",animation:"spin 0.7s linear infinite",flexShrink:0}}></div>
              <span>{syncStatus}</span>
            </div>
          )}
          {syncError && (
            <div style={{marginBottom:12,padding:"8px 14px",background:RED_LIGHT,border:`1px solid #e8c0c0`,borderRadius:4,fontSize:11,color:RED,fontFamily:"sans-serif",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
              <span>{syncError}</span>
              <button onClick={()=>setSyncError("")} style={{flexShrink:0,background:"none",border:"none",cursor:"pointer",color:RED,fontSize:14,lineHeight:1,padding:0}}>×</button>
            </div>
          )}

          {/* Clio disconnected warning — shown when Microsoft is connected but Clio is not */}
          {auth && !clioAuthLoading && !clioAuth && (
            <div style={{marginBottom:12,padding:"10px 14px",background:"#fffbea",border:"1px solid #f0d060",borderRadius:4,fontFamily:"sans-serif",fontSize:11,color:"#7a5c00",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <span>Clio is not connected. Time entries will be generated but cannot be matched to your matters. Connect Clio to enable matter matching.</span>
              <button
                onClick={() => { window.location.href = `${API_URL}/clio/login`; }}
                style={{flexShrink:0,fontFamily:"monospace",fontSize:9,padding:"3px 10px",borderRadius:3,border:"1px solid #c8a020",background:"#fff8e0",color:"#7a5c00",cursor:"pointer",letterSpacing:"0.04em",whiteSpace:"nowrap"}}
              >Connect Clio</button>
            </div>
          )}

          {/* Getting Started panel — shown only when no data synced yet */}
          {!gettingStartedDismissed && calendar.length === 0 && emails.length === 0 && documents.length === 0 && (
            <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:6,padding:"14px 16px",marginBottom:12,position:"relative"}}>
              <button
                onClick={() => { localStorage.setItem('gettingStartedDismissed','true'); setGettingStartedDismissed(true); }}
                style={{position:"absolute",top:10,right:12,background:"none",border:"none",cursor:"pointer",fontSize:16,color:INK_MUTED,lineHeight:1,padding:0}}
                title="Dismiss"
              >×</button>
              <div style={{fontFamily:"monospace",fontSize:10,textTransform:"uppercase",letterSpacing:"0.08em",color:INK_MUTED,marginBottom:10}}>Getting Started</div>
              <ol style={{margin:0,padding:"0 0 0 18px",display:"flex",flexDirection:"column",gap:6}}>
                {[
                  "Connect Microsoft 365 — click the button in the top right",
                  "Connect Clio — link your Clio account to match time entries to your matters and push entries directly. Highly recommended.",
                  "Pick a date — use the date picker to choose the day you want to reconstruct",
                  "Sync from Microsoft 365 — pull your calendar, emails, and documents for that day",
                  "Review your emails — emails are pre-selected based on your calendar events (up to 30). Check or uncheck any emails before analyzing",
                  "Review your documents — documents are pre-selected based on your calendar events. Adjust selections as needed",
                  "Analyze & Generate Entries — click the button to generate draft time entries from your selected data",
                  "Review and edit entries — adjust descriptions, durations, and matter assignments; check Activity Warnings for any gaps or overlaps in your day",
                  "Push to Clio — approve entries to upload them directly to Clio",
                ].map((step, i) => (
                  <li key={i} style={{fontSize:12,fontFamily:"sans-serif",color:INK_MUTED,lineHeight:1.5}}>{step}</li>
                ))}
              </ol>
            </div>
          )}

          <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:6,overflow:"hidden"}}>

            {/* Source tabs */}
            <div style={{display:"flex",borderBottom:`1px solid ${BORDER}`,background:PAPER_WARM,padding:"0 8px"}}>
              <button style={tabStyle("calendar")} onClick={()=>setTab("calendar")}>Calendar</button>
              <button style={tabStyle("email")} onClick={()=>setTab("email")}>Email</button>
              <button style={tabStyle("docs")} onClick={()=>setTab("docs")}>Documents</button>
            </div>

            {/* Calendar tab */}
            {tab==="calendar" && (
              <>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:PAPER_WARM,borderBottom:`1px solid ${BORDER}`}}>
                  <span style={{fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>Calendar Events</span>
                  <span style={{fontFamily:"monospace",fontSize:10,color:GREEN}}>{calendar.length} items</span>
                </div>
                <div style={{maxHeight:440,overflowY:"auto"}}>
                  {calendar.length === 0 && lookbackStart && !syncing && (
                    <div style={{padding:"24px 20px",textAlign:"center",fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>
                      No calendar events found for this date. Add events to your calendar to improve time entry accuracy.
                    </div>
                  )}
                  {calendar.map((e,i) => (
                    <div key={i} style={{padding:"10px 16px",borderBottom:`1px solid ${BORDER}`,fontFamily:"sans-serif"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                        <div style={{fontSize:13,color:INK,fontWeight:500,flex:1,paddingRight:12}}>{e.subject}</div>
                        <div style={{display:"flex",gap:4,alignItems:"center"}}>
                          <div style={{fontFamily:"monospace",fontSize:10,color:GOLD,whiteSpace:"nowrap"}}>{fmtTime(e.start)} – {fmtTime(e.end)}</div>
                          <button onClick={()=>deleteItem("calendar",i)} style={{fontFamily:"monospace",fontSize:9,padding:"1px 6px",borderRadius:3,border:`1px solid #e8c0c0`,background:RED_LIGHT,color:RED,cursor:"pointer"}}>✕</button>
                        </div>
                      </div>
                      <div style={{fontSize:11,color:INK_MUTED,marginBottom:5}}>
                        {fmtDuration(e.start,e.end)} · {e.isOnlineMeeting?"Teams/Video":"In-person/Phone"} · {e.location||"No location"}
                      </div>
                      <div style={{fontSize:11,color:INK_MUTED,marginBottom:5,fontStyle:"italic"}}>
                        Attendees: {e.attendees.map(a=>a.split("@")[0]).join(", ")}
                      </div>
                      {e.bodyPreview && (
                        <div style={{fontSize:11,color:INK_FAINT,fontStyle:"italic",borderLeft:`2px solid ${BORDER}`,paddingLeft:8}}>
                          "{e.bodyPreview}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Email tab */}
            {tab==="email" && (
              <>
                {/* 4b — Vague event banner */}
                {vagueEvents.length > 0 && !vagueEventBannerDismissed && (
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,padding:"8px 14px",background:"#fdf8ec",borderBottom:`1px solid #e8dfc0`,fontFamily:"sans-serif",fontSize:11,color:"#7a6010",lineHeight:1.5}}>
                    <span>
                      Some calendar events were too vague to match emails: <strong>{vagueEvents.map(e=>e.subject||'(untitled)').join(', ')}</strong>. Consider adding client names or matter details to these events.
                    </span>
                    <button onClick={()=>setVagueEventBannerDismissed(true)} style={{flexShrink:0,background:"none",border:"none",color:"#7a6010",cursor:"pointer",fontSize:13,lineHeight:1,padding:"0 2px"}}>✕</button>
                  </div>
                )}

                {/* 4c — Header with selection count */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:PAPER_WARM,borderBottom:`1px solid ${BORDER}`}}>
                  <span style={{fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>Emails</span>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    <span style={{fontFamily:"monospace",fontSize:10,color:INK_MUTED}}>{lookbackLabel(selectedDate, prefs.email_lookback_days ?? 3)}</span>
                    <span style={{fontFamily:"monospace",fontSize:10,color:checkedEmailCount>30?RED:BLUE}}>
                      {checkedEmailCount} of {emails.length} selected
                    </span>
                    {checkedEmailCount > 30 && (
                      <span style={{fontFamily:"sans-serif",fontSize:10,color:RED}}>Maximum 30 emails selected. Deselect some to continue.</span>
                    )}
                  </div>
                </div>

                {/* 4a — Email list with checkboxes */}
                <div style={{maxHeight:440,overflowY:"auto"}}>
                  {emails.length === 0 && lookbackStart && !syncing && (
                    <div style={{padding:"24px 20px",textAlign:"center",fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>
                      No emails found for this date range. Try expanding your lookback period in Settings.
                    </div>
                  )}
                  {[...emails].sort((a,b)=>new Date(a.sentAt||a.receivedAt)-new Date(b.sentAt||b.receivedAt)).map((e,i) => {
                    const isChecked = emailChecked[e.id] === true;
                    return (
                      <div key={i} style={{padding:"10px 16px",borderBottom:`1px solid ${BORDER}`,fontFamily:"sans-serif",opacity:isChecked?1:0.45,transition:"opacity 0.15s"}}>
                        <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={ev => setEmailChecked(prev => ({...prev, [e.id]: ev.target.checked}))}
                            style={{marginTop:4,cursor:"pointer",accentColor:GREEN,flexShrink:0,width:14,height:14}}
                          />
                          <div style={{flex:1}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                              <div style={{display:"flex",alignItems:"center",gap:6}}>
                                <span style={{fontFamily:"monospace",fontSize:9,padding:"1px 6px",borderRadius:3,background:e.folder==="sent"?GOLD_LIGHT:BLUE_LIGHT,color:e.folder==="sent"?GOLD:BLUE,border:`1px solid ${e.folder==="sent"?"#e0c870":"#b0c8e0"}`}}>
                                  {e.folder==="sent"?"SENT":"INBOX"}
                                </span>
                                {e.importance==="high" && <span style={{fontFamily:"monospace",fontSize:9,padding:"1px 6px",borderRadius:3,background:RED_LIGHT,color:RED,border:`1px solid #e8c0c0`}}>HIGH</span>}
                                {e.hasAttachments && <span style={{fontSize:10,color:INK_FAINT}}>📎</span>}
                              </div>
                              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                                <div style={{fontFamily:"monospace",fontSize:10,color:INK_MUTED,textAlign:"right"}}>
                                  {e.activityDate ? new Date(`${e.activityDate}T12:00:00Z`).toLocaleDateString("en-US",{month:"short",day:"numeric",timeZone:"UTC"}) + " · " : ""}{fmtTime(e.sentAt||e.receivedAt)}
                                </div>
                                <button onClick={()=>deleteItem("email",emails.indexOf(e))} style={{fontFamily:"monospace",fontSize:9,padding:"1px 6px",borderRadius:3,border:`1px solid #e8c0c0`,background:RED_LIGHT,color:RED,cursor:"pointer"}}>✕</button>
                              </div>
                            </div>
                            <div style={{fontSize:13,color:INK,fontWeight:500,marginBottom:2}}>{e.subject}</div>
                            {e.matched && Array.isArray(e.matchedEvents) && e.matchedEvents.length > 0 && (
                              <div style={{fontSize:10,color:GREEN,marginBottom:3,fontFamily:"monospace"}}>
                                Matched: {e.matchedEvents.join(', ')}
                              </div>
                            )}
                            <div style={{fontSize:11,color:INK_MUTED,marginBottom:5}}>
                              {e.folder==="sent"?`To: ${e.to.join(", ")}`:`From: ${e.from}`}
                            </div>
                            <div style={{fontSize:11,color:INK_FAINT,fontStyle:"italic",borderLeft:`2px solid ${BORDER}`,paddingLeft:8,lineHeight:1.5}}>
                              "{e.bodyPreview}"
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Documents tab */}
            {tab==="docs" && (
              <>
                {/* Vague event banner — mirrors email tab */}
                {vagueEvents.length > 0 && !vagueEventBannerDismissed && (
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,padding:"8px 14px",background:"#fdf8ec",borderBottom:`1px solid #e8dfc0`,fontFamily:"sans-serif",fontSize:11,color:"#7a6010",lineHeight:1.5}}>
                    <span>
                      Some calendar events were too vague to match documents: <strong>{vagueEvents.map(e=>e.subject||'(untitled)').join(', ')}</strong>. Consider adding client names or matter details to these events.
                    </span>
                    <button onClick={()=>setVagueEventBannerDismissed(true)} style={{flexShrink:0,background:"none",border:"none",color:"#7a6010",cursor:"pointer",fontSize:13,lineHeight:1,padding:"0 2px"}}>✕</button>
                  </div>
                )}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:PAPER_WARM,borderBottom:`1px solid ${BORDER}`}}>
                  <span style={{fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>Documents</span>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    <span style={{fontFamily:"monospace",fontSize:10,color:INK_MUTED}}>{lookbackLabel(selectedDate, prefs.doc_lookback_days ?? 3)}</span>
                    <span style={{fontFamily:"monospace",fontSize:10,color:GOLD}}>
                      {checkedDocCount} of {documents.length} selected
                    </span>
                    <input
                      id="doc-upload-input"
                      type="file"
                      accept=".pdf,.docx,.doc,.txt"
                      multiple
                      style={{display:"none"}}
                      onChange={e => {
                        const files = Array.from(e.target.files);
                        const uploaded = files.map(f => ({
                          id: `upload-${f.name}-${f.lastModified}`,
                          name: f.name,
                          type: f.name.split('.').pop().toLowerCase(),
                          lastModified: new Date(f.lastModified).toISOString(),
                          sizeKB: Math.round(f.size / 1024),
                          path: '/',
                          lastModifiedBy: '',
                          activityDate: new Date(f.lastModified).toLocaleDateString('en-CA'),
                          source: 'upload',
                          matched: false,
                          matchedEvent: '',
                        }));
                        setDocuments(prev => [...prev, ...uploaded]);
                        const newChecked = {};
                        uploaded.forEach(f => { newChecked[f.id] = true; });
                        setDocChecked(prev => ({...prev, ...newChecked}));
                        e.target.value = '';
                      }}
                    />
                    <button
                      onClick={() => document.getElementById('doc-upload-input').click()}
                      style={{fontFamily:"monospace",fontSize:9,padding:"3px 10px",borderRadius:3,border:`1px solid ${GOLD}`,background:"transparent",color:GOLD,cursor:"pointer",letterSpacing:"0.04em"}}
                    >+ Upload</button>
                  </div>
                </div>
                <div style={{maxHeight:440,overflowY:"auto"}}>
                  {documents.length === 0 && lookbackStart && !syncing && (
                    <div style={{padding:"24px 20px",textAlign:"center",fontFamily:"sans-serif",fontSize:12,color:INK_MUTED}}>
                      No documents found for this date range. Try expanding your lookback period in Settings.
                    </div>
                  )}
                  {[...documents].sort((a,b)=>new Date(a.lastModified)-new Date(b.lastModified)).map((d,i) => {
                    const isChecked = docChecked[d.id] === true;
                    return (
                      <div key={i} style={{padding:"10px 16px",borderBottom:`1px solid ${BORDER}`,fontFamily:"sans-serif",opacity:isChecked?1:0.45,transition:"opacity 0.15s"}}>
                        <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={ev => setDocChecked(prev => ({...prev, [d.id]: ev.target.checked}))}
                            style={{marginTop:4,cursor:"pointer",accentColor:GREEN,flexShrink:0,width:14,height:14}}
                          />
                          <div style={{flex:1}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,flex:1,paddingRight:12}}>
                                <span style={{fontFamily:"monospace",fontSize:9,padding:"1px 5px",borderRadius:3,background:d.type==="pdf"?RED_LIGHT:GREEN_LIGHT,color:d.type==="pdf"?RED:GREEN,border:`1px solid ${d.type==="pdf"?"#e8c0c0":"#b8d4bf"}`}}>
                                  {d.type.toUpperCase()}
                                </span>
                                {d.source==="upload" && (
                                  <span style={{fontFamily:"monospace",fontSize:9,padding:"1px 5px",borderRadius:3,background:"#f0f0ff",color:"#6060c0",border:"1px solid #c0c0e0"}}>Uploaded</span>
                                )}
                                <div style={{fontSize:12,color:INK,fontWeight:500,fontFamily:"sans-serif",wordBreak:"break-all"}}>{d.name}</div>
                              </div>
                              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                                <div style={{fontFamily:"monospace",fontSize:10,color:GOLD,whiteSpace:"nowrap",textAlign:"right"}}>
                                  {d.activityDate ? new Date(`${d.activityDate}T12:00:00Z`).toLocaleDateString("en-US",{month:"short",day:"numeric",timeZone:"UTC"}) + " · " : ""}{fmtTime(d.lastModified)}
                                </div>
                                <button onClick={()=>deleteItem("docs",documents.indexOf(d))} style={{fontFamily:"monospace",fontSize:9,padding:"1px 6px",borderRadius:3,border:`1px solid #e8c0c0`,background:RED_LIGHT,color:RED,cursor:"pointer"}}>✕</button>
                              </div>
                            </div>
                            {d.matched && d.matchedEvent && (
                              <div style={{fontSize:10,color:GREEN,marginBottom:3,fontFamily:"monospace"}}>
                                Matched: {d.matchedEvent}
                              </div>
                            )}
                            <div style={{fontSize:11,color:INK_MUTED}}>
                              {d.source==="upload"
                                ? `${d.sizeKB}KB · Uploaded`
                                : `Path: ${d.path} · ${d.sizeKB}KB · Modified by: ${d.lastModifiedBy.split("@")[0]}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Data summary footer */}
            <div style={{display:"flex",gap:0,borderTop:`1px solid ${BORDER}`,background:PAPER_WARM}}>
              {[
                [GREEN, `${calendar.length} calendar events`],
                [BLUE, `${emails.length} emails`],
                [GOLD, `${documents.length} documents`],
              ].map(([color,label]) => (
                <div key={label} style={{flex:1,padding:"8px 12px",borderRight:`1px solid ${BORDER}`,display:"flex",alignItems:"center",gap:5}}>
                  <div style={{width:5,height:5,borderRadius:"50%",background:color}}></div>
                  <span style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,letterSpacing:"0.04em"}}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Generate button */}
          {(() => {
            const tooManySelected = emails.length > 0 && checkedEmailCount > 30;
            const isDisabled = loading || syncing || tooManySelected;
            return (
              <button
                onClick={generate}
                disabled={isDisabled}
                style={{
                  width:"100%",marginTop:14,padding:14,
                  background:isDisabled?"#888":GREEN,
                  color:"white",border:"none",borderRadius:5,
                  fontFamily:"sans-serif",fontSize:13,fontWeight:500,
                  cursor:isDisabled?"not-allowed":"pointer",letterSpacing:"0.02em",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  transition:"background 0.2s"
                }}
              >
                {loading && <div style={{width:13,height:13,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"white",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}></div>}
                {loading ? (STATUS_LABELS[generateStatus] || "Analyzing 3 data sources...") : "Analyze & Generate Entries"}
              </button>
            );
          })()}
        </div>

        {/* RIGHT — Output */}
        <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:6,overflow:"hidden",position:"sticky",top:20}}>

          <div style={{background:PAPER_WARM,borderBottom:`1px solid ${BORDER}`,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontFamily:"Georgia,serif",fontSize:15,color:INK}}>Time Entries</span>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              {generated && <span style={{fontFamily:"monospace",fontSize:10,color:GREEN}}>{entries.length} entries · {totalHours.toFixed(1)}h billable</span>}
              <div
                onClick={()=>{ if(!editingRate) toggleEditRate(); }}
                style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"white",border:`1px solid ${editingRate?GOLD:BORDER}`,borderRadius:6,padding:"4px 10px",minWidth:52,textAlign:"center",cursor:editingRate?"default":"pointer",transition:"border-color 0.15s"}}
                onMouseEnter={e=>{ if(!editingRate) e.currentTarget.style.borderColor=GOLD; }}
                onMouseLeave={e=>{ if(!editingRate) e.currentTarget.style.borderColor=BORDER; }}
              >
                {editingRate ? (
                  <input
                    type="number"
                    min="25"
                    step="25"
                    value={editRateText}
                    onChange={e=>setEditRateText(e.target.value)}
                    onKeyDown={e=>{ if(e.key==='Enter') toggleEditRate(); if(e.key==='Escape') setEditingRate(false); }}
                    onBlur={toggleEditRate}
                    autoFocus
                    style={{width:44,fontFamily:"Georgia,serif",fontSize:14,fontWeight:700,color:INK,textAlign:"center",border:"none",outline:"none",background:"transparent",padding:0,lineHeight:1}}
                  />
                ) : (
                  <div style={{fontFamily:"Georgia,serif",fontSize:16,fontWeight:700,color:INK,lineHeight:1}}>${prefs.hourly_rate||300}</div>
                )}
                <div style={{fontFamily:"monospace",fontSize:8,color:INK_MUTED,letterSpacing:"0.06em",marginTop:2,textTransform:"uppercase"}}>rate/hr</div>
              </div>
            </div>
          </div>

          {/* Summary */}
          {generated && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",borderBottom:`1px solid ${BORDER}`}}>
              {[[totalHours.toFixed(1),"Billable hours",GREEN],[`$${totalValue.toLocaleString()}`,`Est. value @ $${hourlyRate}/hr`,INK],[`${billable.filter((_,i)=>approved[i]).length}/${billable.length}`,"Entries approved",GOLD]].map(([v,l,c])=>(
                <div key={l} style={{padding:"12px 14px",borderRight:`1px solid ${BORDER}`,textAlign:"center"}}>
                  <div style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:700,color:c}}>{v}</div>
                  <div style={{fontFamily:"monospace",fontSize:9,textTransform:"uppercase",letterSpacing:"0.07em",color:INK_MUTED,marginTop:2}}>{l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Placeholder */}
          {!generated && !loading && !error && (
            <div style={{padding:"52px 24px",textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:12,opacity:0.25}}>⚖</div>
              <div style={{fontFamily:"Georgia,serif",fontSize:15,color:INK_FAINT,fontStyle:"italic"}}>Awaiting analysis</div>
              <div style={{fontSize:12,color:INK_FAINT,marginTop:8,fontFamily:"sans-serif",lineHeight:1.6}}>
                2ndCounsel will cross-reference all three data sources,<br/>infer matter assignments, and draft billing narratives
              </div>
              <div style={{marginTop:20,display:"flex",justifyContent:"center",gap:16}}>
                {[[GREEN,"Calendar"],[BLUE,"Email"],[GOLD,"Documents"]].map(([c,l])=>(
                  <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:INK_MUTED,fontFamily:"monospace"}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:c}}></div>{l}
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div style={{padding:"52px 24px",textAlign:"center"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:8}}>
                <div style={{width:14,height:14,border:`2px solid ${BORDER}`,borderTopColor:GREEN,borderRadius:"50%",animation:"spin 0.7s linear infinite",flexShrink:0}}></div>
                <div style={{fontFamily:"Georgia,serif",fontSize:14,color:INK_MUTED,fontStyle:"italic"}}>{STATUS_LABELS[generateStatus] || `Cross-referencing ${calendar.length + emails.length + documents.length} data points...`}</div>
              </div>
              <div style={{fontSize:11,color:INK_FAINT,marginTop:8,fontFamily:"monospace"}}>Inferring matter assignments · Estimating time · Drafting narratives</div>
            </div>
          )}

          {error && (
            <div style={{margin:16,padding:"12px 16px",background:RED_LIGHT,border:"1px solid #e8c0c0",borderRadius:4,fontSize:12,color:RED,fontFamily:"sans-serif",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
              <span>{error}</span>
              <button onClick={()=>setError("")} style={{flexShrink:0,background:"none",border:"none",cursor:"pointer",color:RED,fontSize:16,lineHeight:1,padding:0}}>×</button>
            </div>
          )}

          {/* Entries */}
          {generated && (
            <div style={{padding:"14px 16px",maxHeight:560,overflowY:"auto"}}>
              {(() => {
                const warningFlags = entries.flatMap(e => (e.flags||[]).filter(f => /gap|overlap/i.test(f)));
                return warningFlags.length > 0 ? (
                  <div style={{marginBottom:12,padding:"10px 14px",background:"#fffbea",border:"1px solid #f0d060",borderRadius:5,fontFamily:"sans-serif",fontSize:11,color:"#7a5c00"}}>
                    <div style={{fontWeight:700,marginBottom:6}}>⚠ Activity Warnings</div>
                    {warningFlags.map((f,i)=><div key={i} style={{marginBottom:2}}>{f}</div>)}
                  </div>
                ) : null;
              })()}
              {entries.map((entry,i) => {
                const ms = MATTER_STYLE[entry.matterCode] || MATTER_STYLE.NB;
                const isApproved = approved[i];
                const flagExpanded = showFullFlag[i];
                const isEditing = editing[i];
                const showSrc = showReasoning[i];
                return (
                  <div key={i} style={{border:`1px solid ${isApproved?"#2d5a3d":BORDER}`,borderRadius:5,marginBottom:12,overflow:"hidden",background:isApproved?GREEN_LIGHT:"white",transition:"all 0.2s"}}>

                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",background:PAPER_WARM,borderBottom:`1px solid ${BORDER}`}}>
                      <div style={{flex:1,paddingRight:12}}>
                        {editingMatter[i] ? (
                          <MatterTypeahead
                            value={entry.matter}
                            matters={clioMatters}
                            textColor={ms.text}
                            onSave={text => {
                              const val = text.trim();
                              if (val) setEntries(prev => prev.map((e,idx) => idx===i ? {...e,matter:val} : e));
                              setEditingMatter(m => ({...m,[i]:false}));
                            }}
                            onCancel={() => setEditingMatter(m => ({...m,[i]:false}))}
                          />
                        ) : (
                          (() => {
                            const parts = entry.matter.split(" — ");
                            const matterNum = parts[0] || entry.matter;
                            const matterName = parts.slice(1).join(" — ") || "";
                            return (
                              <div
                                onClick={()=>{ setEditMatterText(t=>({...t,[i]:entry.matter})); setEditingMatter(m=>({...m,[i]:true})); }}
                                style={{cursor:"pointer",borderRadius:3,padding:"2px 4px",margin:"-2px -4px",transition:"background 0.15s"}}
                                onMouseEnter={e=>e.currentTarget.style.background=GOLD_LIGHT}
                                onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                              >
                                <div style={{fontFamily:"sans-serif",fontSize:14,fontWeight:700,color:ms.text,lineHeight:1.3}}>{matterName || matterNum}</div>
                                {matterName && (
                                  <div style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,letterSpacing:"0.04em",marginTop:2}}>{matterNum}</div>
                                )}
                              </div>
                            );
                          })()
                        )}
                        <div style={{display:"flex",gap:6,marginTop:5,alignItems:"flex-start"}}>
                          <div style={{display:"flex",alignItems:"flex-start",gap:4}}>
                            <div style={{width:7,height:7,borderRadius:"50%",background:CONF_DOT[entry.confidence]||CONF_DOT.medium,flexShrink:0,marginTop:2}}></div>
                            {entry.flags&&entry.flags.length>0&&(
                              <div style={{fontFamily:"sans-serif",fontSize:10,color:INK_MUTED}}>
                                <span style={flagExpanded?{}:{display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                                  {entry.flags.map((f,fi)=><span key={fi} style={{display:"block"}}>{f}</span>)}
                                </span>
                                <button onClick={()=>setShowFullFlag(f=>({...f,[i]:!f[i]}))} style={{marginLeft:4,fontFamily:"sans-serif",fontSize:10,color:BLUE,background:"none",border:"none",cursor:"pointer",padding:0,whiteSpace:"nowrap"}}>
                                  {flagExpanded?"Show less":"Show more"}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <div
                          onClick={()=>{ if(!editingHours[i]) toggleEditHours(i); }}
                          style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"white",border:`1px solid ${editingHours[i]?GOLD:BORDER}`,borderRadius:6,padding:"6px 12px",minWidth:54,textAlign:"center",cursor:editingHours[i]?"default":"pointer",transition:"border-color 0.15s"}}
                          onMouseEnter={e=>{ if(!editingHours[i]) e.currentTarget.style.borderColor=GOLD; }}
                          onMouseLeave={e=>{ if(!editingHours[i]) e.currentTarget.style.borderColor=BORDER; }}
                        >
                          {editingHours[i] ? (
                            <input
                              type="number"
                              min="0.1"
                              max="24"
                              step="0.1"
                              value={editHoursText[i]??entry.hours.toFixed(1)}
                              onChange={e=>setEditHoursText(t=>({...t,[i]:e.target.value}))}
                              onKeyDown={e=>{ if(e.key==='Enter') toggleEditHours(i); if(e.key==='Escape') setEditingHours(h=>({...h,[i]:false})); }}
                              onBlur={()=>toggleEditHours(i)}
                              autoFocus
                              style={{width:44,fontFamily:"Georgia,serif",fontSize:18,fontWeight:700,color:INK,textAlign:"center",border:"none",outline:"none",background:"transparent",padding:0,lineHeight:1}}
                            />
                          ) : (
                            <div style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:700,color:INK,lineHeight:1}}>{entry.hours.toFixed(1)}</div>
                          )}
                          <div style={{fontFamily:"monospace",fontSize:8,color:INK_MUTED,letterSpacing:"0.06em",marginTop:2,textTransform:"uppercase"}}>hrs</div>
                        </div>
                        {entry.matterCode !== "NB" && (
                          <div style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,textAlign:"center",whiteSpace:"nowrap"}}>
                            ${Math.round((editingHours[i] ? (parseFloat(editHoursText[i]) || entry.hours) : entry.hours) * hourlyRate).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{padding:"10px 12px"}}>
                      {isEditing ? (
                        <div style={{position:"relative"}}>
                          <textarea value={editText[i]??entry.narrative} onChange={e=>setEditText(t=>({...t,[i]:e.target.value}))} style={{width:"100%",fontFamily:"Georgia,serif",fontSize:12.5,lineHeight:1.7,color:INK,fontStyle:"italic",border:`2px solid ${GOLD}`,borderRadius:3,padding:6,paddingBottom:18,background:GOLD_LIGHT,resize:"vertical",minHeight:80,boxSizing:"border-box"}}/>
                          <div style={{position:"absolute",bottom:4,right:6,pointerEvents:"none",display:"flex",gap:1,opacity:0.45}}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <line x1="3" y1="10" x2="10" y2="3" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round"/>
                              <line x1="6" y1="10" x2="10" y2="6" stroke={GOLD} strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <div style={{fontFamily:"Georgia,serif",fontSize:12.5,lineHeight:1.7,color:INK,fontStyle:"italic"}}>{entry.narrative}</div>
                      )}
                    </div>

                    {/* Sources used */}
                    {entry.sourcesUsed && entry.sourcesUsed.length > 0 && (
                      <div style={{padding:"0 12px 8px"}}>
                        <button onClick={()=>setShowReasoning(s=>({...s,[i]:!s[i]}))} style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,background:"none",border:"none",cursor:"pointer",letterSpacing:"0.04em",padding:0,textDecoration:"underline"}}>
                          {showSrc?"Hide":"Show"} sources ({entry.sourcesUsed.length} used)
                        </button>
                        {showSrc && (
                          <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:3}}>
                            {entry.sourcesUsed.map(s=>(
                              <span key={s.id || s} style={{fontFamily:"sans-serif",fontSize:11,padding:"3px 8px",borderRadius:3,background:PAPER_WARM,color:INK_MUTED,border:`1px solid ${BORDER}`}}>
                                {s.label || s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{display:"flex",gap:6,padding:"7px 12px",borderTop:`1px solid ${BORDER}`,background:PAPER,flexWrap:"wrap"}}>
                      <button onClick={()=>toggleApprove(i)} style={{fontSize:11,padding:"4px 10px",borderRadius:3,border:`1px solid ${isApproved?"#2d5a3d":BORDER}`,background:isApproved?GREEN:GREEN_LIGHT,color:isApproved?"white":GREEN,cursor:"pointer",fontFamily:"monospace",letterSpacing:"0.03em",transition:"all 0.15s"}}>
                        {isApproved?"Approved":"Approve"}
                      </button>
                      <button onClick={()=>toggleEdit(i)} style={{fontSize:11,padding:"4px 10px",borderRadius:3,border:`1px solid ${BORDER}`,background:isEditing?GOLD:"white",color:isEditing?"white":INK_MUTED,cursor:"pointer",fontFamily:"monospace",letterSpacing:"0.03em"}}>
                        {isEditing?"Save":"Edit"}
                      </button>
                      <button onClick={()=>copyEntry(i)} style={{fontSize:11,padding:"4px 10px",borderRadius:3,border:`1px solid ${BORDER}`,background:"white",color:INK_MUTED,cursor:"pointer",fontFamily:"monospace",letterSpacing:"0.03em"}}>
                        {copied[i]?"Copied!":"Copy"}
                      </button>
                      <button onClick={()=>dismissEntry(i)} title="Dismiss entry" style={{fontSize:11,padding:"4px 8px",borderRadius:3,border:`1px solid #e8c0c0`,background:RED_LIGHT,color:RED,cursor:"pointer",fontFamily:"monospace",letterSpacing:"0.03em"}}>✕</button>
                      {clioAuth && isApproved && entry.matterCode !== "NB" && !pushResult[i]?.success && (
                        <button
                          onClick={()=>setPushModal({ entryIdx:i, matterId: clioMatters[0]?.id || "" })}
                          disabled={pushing[i]}
                          style={{fontSize:11,padding:"4px 10px",borderRadius:3,border:"1px solid #7ba7d4",background:pushing[i]?"#eef3f9":"#1a2a3a",color:pushing[i]?BLUE:"#7ba7d4",cursor:pushing[i]?"default":"pointer",fontFamily:"monospace",letterSpacing:"0.03em",marginLeft:"auto",transition:"all 0.15s"}}
                        >
                          {pushing[i]?"Pushing...":"Push to Clio"}
                        </button>
                      )}
                      {pushResult[i]?.success && (
                        <a href={pushResult[i].clioUrl} target="_blank" rel="noreferrer"
                          style={{fontSize:11,padding:"4px 10px",borderRadius:3,border:"1px solid #2d5a3d",background:GREEN_LIGHT,color:GREEN,fontFamily:"monospace",letterSpacing:"0.03em",marginLeft:"auto",textDecoration:"none"}}
                        >Pushed to Clio</a>
                      )}
                      {pushResult[i]?.success===false && (
                        <span style={{fontSize:10,color:RED,fontFamily:"monospace",marginLeft:"auto",alignSelf:"center"}}>Push failed: {pushResult[i].error}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {generated && entries.length > 0 && (
            <div style={{padding:"8px 16px",borderTop:`1px solid ${BORDER}`,background:PAPER_WARM,textAlign:"center",fontFamily:"monospace",fontSize:10,color:INK_MUTED}}>
              Total: {totalHours.toFixed(1)} hrs @${hourlyRate} · <strong style={{color:INK}}>${totalValue.toLocaleString()}</strong>
            </div>
          )}

          {generated && (
            <div style={{display:"flex",gap:8,padding:"12px 16px",borderTop:`1px solid ${BORDER}`,background:PAPER_WARM}}>
              <button onClick={copyAll} style={{flex:1,padding:"8px",borderRadius:4,border:`1px solid ${BORDER}`,background:"white",fontSize:12,fontFamily:"monospace",cursor:"pointer",color:INK,letterSpacing:"0.03em"}}>
                {copied.all?"Copied!":"Copy All"}
              </button>
              <button onClick={exportCSV} style={{flex:1,padding:"8px",borderRadius:4,border:"none",background:GREEN,fontSize:12,fontFamily:"monospace",cursor:"pointer",color:"white",letterSpacing:"0.03em"}}>
                Export CSV
              </button>
            </div>
          )}

          <div style={{padding:"8px 16px",fontFamily:"monospace",fontSize:9,color:INK_FAINT,letterSpacing:"0.06em",textTransform:"uppercase",textAlign:"center",borderTop:`1px solid ${BORDER}`}}>
            2ndCounsel · Attorney Intelligence Platform
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Settings Modal */}
      {showSettings && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowSettings(false)}>
          <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:10,width:420,maxHeight:"calc(100vh - 48px)",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 8px 32px rgba(0,0,0,0.25)"}} onClick={e=>e.stopPropagation()}>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px",background:PAPER_WARM,borderBottom:`1px solid ${BORDER}`,flexShrink:0}}>
              <span style={{fontFamily:"Georgia,serif",fontSize:15,color:INK}}>Settings</span>
              <button onClick={()=>setShowSettings(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:INK_MUTED,lineHeight:1,padding:2}}>✕</button>
            </div>

            {prefsLoading ? (
              <div style={{padding:"32px",textAlign:"center",fontFamily:"sans-serif",fontSize:13,color:INK_MUTED}}>Loading…</div>
            ) : (
              <div style={{padding:"20px 18px",display:"flex",flexDirection:"column",gap:18,overflowY:"auto",flex:1}}>

                {/* Daily Digest Email section label */}
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0"}}>
                  <div style={{flex:1,height:1,background:BORDER}}></div>
                  <span style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,letterSpacing:"0.08em",textTransform:"uppercase"}}>Daily Digest Email</span>
                  <div style={{flex:1,height:1,background:BORDER}}></div>
                </div>

                {/* Toggle */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontFamily:"sans-serif",fontSize:13,color:INK,fontWeight:500}}>Send me a daily digest email</span>
                  <div
                    onClick={()=>setPrefs(p=>({...p,digest_enabled:!p.digest_enabled}))}
                    style={{width:42,height:24,borderRadius:12,background:prefs.digest_enabled?GREEN:"#ccc",cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}
                  >
                    <div style={{position:"absolute",top:3,left:prefs.digest_enabled?20:3,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left 0.2s"}}></div>
                  </div>
                </div>

                {/* Time picker */}
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontFamily:"sans-serif",fontSize:12,color:prefs.digest_enabled?INK_MUTED:"#bbb",fontWeight:500,textTransform:"uppercase",letterSpacing:"0.05em"}}>Send at</label>
                  <select
                    value={prefs.digest_time}
                    onChange={e=>setPrefs(p=>({...p,digest_time:e.target.value}))}
                    disabled={!prefs.digest_enabled}
                    style={{fontFamily:"sans-serif",fontSize:13,padding:"8px 10px",border:`1px solid ${prefs.digest_enabled?BORDER:"#e0e0e0"}`,borderRadius:5,background:prefs.digest_enabled?"white":"#f5f5f5",color:prefs.digest_enabled?INK:"#bbb",cursor:prefs.digest_enabled?"pointer":"default"}}
                  >
                    {Array.from({length:33},(_,i)=>{
                      const totalMins = 360 + i*30;
                      const h24 = Math.floor(totalMins/60);
                      const mm = String(totalMins%60).padStart(2,"0");
                      const h12 = h24%12||12;
                      const ampm = h24<12?"AM":"PM";
                      const val = `${String(h24).padStart(2,"0")}:${mm}`;
                      return <option key={val} value={val}>{`${h12}:${mm} ${ampm}`}</option>;
                    })}
                  </select>
                </div>

                {/* Data Settings section label */}
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0"}}>
                  <div style={{flex:1,height:1,background:BORDER}}></div>
                  <span style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,letterSpacing:"0.08em",textTransform:"uppercase"}}>Data Settings</span>
                  <div style={{flex:1,height:1,background:BORDER}}></div>
                </div>

                {/* Timezone */}
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontFamily:"sans-serif",fontSize:12,color:INK_MUTED,fontWeight:500,textTransform:"uppercase",letterSpacing:"0.05em"}}>My Timezone</label>
                  <select
                    value={prefs.timezone}
                    onChange={e=>setPrefs(p=>({...p,timezone:e.target.value}))}
                    style={{fontFamily:"sans-serif",fontSize:13,padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:5,background:"white",color:INK,cursor:"pointer"}}
                  >
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                  </select>
                </div>

                {/* Email lookback */}
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontFamily:"sans-serif",fontSize:12,color:INK_MUTED,fontWeight:500,textTransform:"uppercase",letterSpacing:"0.05em"}}>Email history to analyze</label>
                  <select
                    value={prefs.email_lookback_days ?? 3}
                    onChange={e=>setPrefs(p=>({...p,email_lookback_days:Number(e.target.value)}))}
                    style={{fontFamily:"sans-serif",fontSize:13,padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:5,background:"white",color:INK,cursor:"pointer"}}
                  >
                    {[1,2,3,4,5,6,7].map(n=><option key={n} value={n}>{n === 1 ? "1 day" : `${n} days`}</option>)}
                  </select>
                </div>

                {/* Document lookback */}
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontFamily:"sans-serif",fontSize:12,color:INK_MUTED,fontWeight:500,textTransform:"uppercase",letterSpacing:"0.05em"}}>Document history to analyze</label>
                  <select
                    value={prefs.doc_lookback_days ?? 3}
                    onChange={e=>setPrefs(p=>({...p,doc_lookback_days:Number(e.target.value)}))}
                    style={{fontFamily:"sans-serif",fontSize:13,padding:"8px 10px",border:`1px solid ${BORDER}`,borderRadius:5,background:"white",color:INK,cursor:"pointer"}}
                  >
                    {[1,2,3,4,5,6,7].map(n=><option key={n} value={n}>{n === 1 ? "1 day" : `${n} days`}</option>)}
                  </select>
                </div>

                {/* Email Filters section label */}
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0"}}>
                  <div style={{flex:1,height:1,background:BORDER}}></div>
                  <span style={{fontFamily:"monospace",fontSize:9,color:INK_MUTED,letterSpacing:"0.08em",textTransform:"uppercase"}}>Email Filters</span>
                  <div style={{flex:1,height:1,background:BORDER}}></div>
                </div>

                {/* Clear email filters */}
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  <button
                    onClick={clearCacheAll}
                    disabled={cacheClearLoading}
                    style={{width:"100%",padding:"8px",borderRadius:5,border:`1px solid #e8c0c0`,background:RED_LIGHT,fontFamily:"sans-serif",fontSize:12,color:cacheClearLoading?INK_FAINT:RED,cursor:cacheClearLoading?"not-allowed":"pointer",letterSpacing:"0.01em"}}
                  >
                    {cacheClearLoading ? 'Clearing…' : 'Clear Email Filters'}
                  </button>
                </div>

                {/* Success / error message */}
                {cacheClearMsg && (
                  <div style={{padding:"8px 12px",borderRadius:4,background:cacheClearMsg.startsWith('Failed')?RED_LIGHT:GREEN_LIGHT,border:`1px solid ${cacheClearMsg.startsWith('Failed')?'#e8c0c0':'#b8d4bf'}`,fontFamily:"sans-serif",fontSize:12,color:cacheClearMsg.startsWith('Failed')?RED:GREEN}}>
                    {cacheClearMsg}
                  </div>
                )}

              </div>
            )}

            {/* Save & Close — pinned footer, always visible */}
            {!prefsLoading && (
              <div style={{padding:"12px 18px",borderTop:`1px solid ${BORDER}`,background:PAPER_WARM,flexShrink:0}}>
                <button
                  onClick={savePrefs}
                  style={{width:"100%",padding:"10px",borderRadius:5,border:"none",background:GREEN,color:"white",fontFamily:"sans-serif",fontSize:13,fontWeight:600,cursor:"pointer",letterSpacing:"0.02em",transition:"opacity 0.15s"}}
                >
                  Save & Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Push to Clio Modal */}
      {pushModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setPushModal(null)}>
          <div style={{background:PAPER_CARD,border:`1px solid ${BORDER}`,borderRadius:8,width:520,overflow:"hidden",boxShadow:"0 8px 32px rgba(0,0,0,0.25)"}} onClick={e=>e.stopPropagation()}>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:"#1a2a3a",borderBottom:`1px solid #2a3a4a`}}>
              <span style={{fontFamily:"monospace",fontSize:10,textTransform:"uppercase",letterSpacing:"0.1em",color:"#7ba7d4"}}>Push Time Entry to Clio</span>
              <button onClick={()=>setPushModal(null)} style={{fontFamily:"monospace",fontSize:12,background:"none",border:"none",cursor:"pointer",color:"#7ba7d4"}}>✕</button>
            </div>

            <div style={{padding:"16px"}}>

              {/* Entry preview */}
              <div style={{background:PAPER_WARM,border:`1px solid ${BORDER}`,borderRadius:4,padding:"10px 12px",marginBottom:14}}>
                <div style={{fontFamily:"monospace",fontSize:10,color:INK_MUTED,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.07em"}}>
                  {entries[pushModal.entryIdx]?.hours.toFixed(1)} hrs · {selectedDate}
                </div>
                <div style={{fontFamily:"Georgia,serif",fontSize:12.5,color:INK,fontStyle:"italic",lineHeight:1.6}}>
                  {entries[pushModal.entryIdx]?.narrative}
                </div>
              </div>

              {/* Matter selector */}
              <label style={{fontFamily:"monospace",fontSize:10,textTransform:"uppercase",letterSpacing:"0.07em",color:INK_MUTED,display:"block",marginBottom:6}}>
                Select Matter
              </label>
              {clioMatters.length > 0 ? (
                <select
                  value={pushModal.matterId}
                  onChange={e=>setPushModal(m=>({...m,matterId:e.target.value}))}
                  style={{width:"100%",fontFamily:"monospace",fontSize:11,padding:"7px 10px",border:`1px solid ${BORDER}`,borderRadius:4,background:"white",color:INK,marginBottom:14}}
                >
                  <option value="">— Select a matter —</option>
                  {clioMatters.map(m=>(
                    <option key={m.id} value={m.id}>
                      {m.number} · {m.client} · {m.description}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{fontFamily:"monospace",fontSize:11,color:INK_MUTED,padding:"8px 0",marginBottom:14}}>
                  No Clio matters loaded. Generate entries with Clio connected first.
                </div>
              )}

              <div style={{fontSize:11,color:INK_FAINT,fontFamily:"sans-serif",marginBottom:14,lineHeight:1.5}}>
                This will create an unbilled draft time entry in Clio. You can review and edit it in Clio before billing.
              </div>

              <div style={{display:"flex",gap:8}}>
                <button
                  onClick={()=>pushToClio(pushModal.entryIdx, pushModal.matterId)}
                  disabled={!pushModal.matterId || pushing[pushModal.entryIdx]}
                  style={{flex:1,padding:"9px",borderRadius:4,border:"none",background:pushModal.matterId?"#1a2a3a":"#ccc",color:pushModal.matterId?"#7ba7d4":"#999",fontFamily:"monospace",fontSize:11,cursor:pushModal.matterId?"pointer":"default",letterSpacing:"0.04em"}}
                >
                  {pushing[pushModal.entryIdx]?"Pushing...":"Confirm Push to Clio"}
                </button>
                <button
                  onClick={()=>setPushModal(null)}
                  style={{padding:"9px 18px",borderRadius:4,border:`1px solid ${BORDER}`,background:"white",color:INK_MUTED,fontFamily:"monospace",fontSize:11,cursor:"pointer"}}
                >Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}